import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Combobox as HUICombobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

import {
    markFieldFocus,
    trackSelection,
    uniqueIdTransfer,
    useFieldValidation,
} from '@/apiCalls/uniqueIDTransferHelper';
import { EDisplays, selectDisplay } from '@/context/display';
import { useAppSelector } from '@/context/storeHooks';
import { formatSpacedStrings } from '@/utils/formatSpacedStrings';

import { EEventType } from '../eventType';
import { FieldLabel } from '../FieldLabel';

export interface ICombobox {
    options: IComboboxOption[];
    placeholder: string;
    heading: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    query: string;
    onSelect: (option: IComboboxOption) => void;
    showErrors: boolean;
    loading?: boolean;
    baseId?: string;
    eventType?: EEventType;
    tooltipContent?: string;
    /** Field wrappers are required by default; optional fields opt out. */
    required?: boolean;
    optionsClassName?: string;
}

export const testId = 'Combobox';
export interface IComboboxOption {
    id: number;
    value: string;
    code?: string;
}

export function Combobox({
    options,
    loading,
    placeholder,
    query,
    onChange,
    onSelect,
    heading,
    showErrors,
    baseId,
    eventType = EEventType.DROPDOWN,
    tooltipContent,
    required = true,
    optionsClassName,
}: ICombobox): JSX.Element {
    const fieldId = `${baseId}-dropdown-${formatSpacedStrings(heading)}`;
    useFieldValidation(fieldId, showErrors);

    const [selected, setSelected] = useState<IComboboxOption | ''>('');
    const display = useAppSelector(selectDisplay);
    const vehicleDisplay = display === EDisplays.VEHICLE;

    const filteredOptions =
        query === ''
            ? options
            : options.filter((person) => {
                  if (typeof person.value === 'string') {
                      return person.value
                          .toLowerCase()
                          .replace(/\s+/g, '')
                          .includes(query.toLowerCase().replace(/\s+/g, ''));
                  }
                  return false;
              });

    function chooseOption(option: IComboboxOption): void {
        setSelected(option);
        onSelect(option);
        uniqueIdTransfer({
            eventType: eventType,
            applicationData: {
                label: `${baseId}-dropdown-${formatSpacedStrings(heading)}`,
            },
        });
        trackSelection(fieldId);
    }
    useEffect(() => {
        setSelected('');
    }, [query]);

    const fieldRef = useRef<HTMLDivElement>(null);
    const [openUpwards, setOpenUpwards] = useState(false);

    // The list is 240px at most. If the field sits closer than that to the
    // bottom of whatever would clip it, the list would run past that edge, so
    // it opens upwards instead. Re-measured on scroll and resize while it is
    // open.
    useLayoutEffect(() => {
        /*
         * The edge that actually cuts the list off, which is not always the
         * window: inside a modal it is the modal's own scroll box, and
         * measuring the viewport there said there was room when there was
         * none — so the list opened downwards and was clipped mid-option.
         */
        function clipBounds(el: HTMLElement): { top: number; bottom: number } {
            let node: HTMLElement | null = el.parentElement;
            while (node) {
                const { overflowY } = window.getComputedStyle(node);
                if (
                    overflowY === 'auto' ||
                    overflowY === 'scroll' ||
                    overflowY === 'hidden'
                ) {
                    const box = node.getBoundingClientRect();
                    return { top: box.top, bottom: box.bottom };
                }
                node = node.parentElement;
            }
            return { top: 0, bottom: window.innerHeight };
        }

        function place(): void {
            const el = fieldRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const bounds = clipBounds(el);
            const below = bounds.bottom - rect.bottom;
            const above = rect.top - bounds.top;
            setOpenUpwards(below < 240 && above > below);
        }
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, []);

    return (
        <div className='py-2' data-testid={testId}>
            <FieldLabel
                title={heading}
                tooltipContent={tooltipContent}
                required={required}
            />

            <HUICombobox
                value={selected}
                onChange={chooseOption}
                data-testid='combobox'
            >
                <div className='relative' ref={fieldRef}>
                    <div
                        className={`border relative w-full cursor-default overflow-hidden rounded-[10px] h-11 bg-white text-left inputButtonFocus ${
                            showErrors && 'border-errorSolid'
                        }`}
                    >
                        <HUICombobox.Input
                            onFocus={(): void => markFieldFocus(fieldId)}
                            className={`placeholderStyle h-full w-full border-none bg-transparent pl-3 pr-10 leading-tight text-textPrimary focus:outline-none ${
                                showErrors && 'bg-error'
                            }`}
                            displayValue={(option: IComboboxOption): string =>
                                option?.value ?? query
                            }
                            id={`${baseId}-dropdown-${formatSpacedStrings(
                                heading
                            )}`}
                            placeholder={placeholder}
                            data-testid='combobox-input'
                            onChange={onChange}
                        />
                        <HUICombobox.Button
                            data-testid='combobox-button'
                            className='absolute inset-y-0 right-0 _flex items-center pr-2'
                        >
                            <ChevronDownIcon
                                className='h-5 w-5 text-gray-400'
                                aria-hidden='true'
                            />
                        </HUICombobox.Button>
                    </div>
                    <Transition
                        as={Fragment}
                        leave='transition ease-in duration-100'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <HUICombobox.Options
                            className={
                                optionsClassName ||
                                `absolute z-[60] max-h-60 w-full overflow-auto rounded-[10px] border border-gray-200 bg-white p-1 shadow-lg focus:outline-none ${
                                    openUpwards
                                        ? 'bottom-full mb-1'
                                        : 'top-full mt-1'
                                }`
                            }
                        >
                            {filteredOptions?.length === 0 ? (
                                <div className='select-none px-3 py-3 text-textBody'>
                                    {loading ? 'Loading...' : 'Nothing found.'}
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <HUICombobox.Option
                                        data-testid='combobox-option'
                                        key={option.id}
                                        className={({
                                            selected,
                                            active,
                                        }): string =>
                                            `relative _flex min-h-[44px] cursor-pointer select-none items-center rounded-[7px] px-3 transition-colors duration-100 ${
                                                active
                                                    ? 'bg-[color-mix(in_srgb,var(--secondaryColor)_8%,transparent)]'
                                                    : ''
                                            } ${
                                                selected
                                                    ? 'text-primary font-semibold'
                                                    : 'text-textPrimary'
                                            }`
                                        }
                                        value={option}
                                    >
                                        {({
                                            selected,
                                            active,
                                        }): JSX.Element => (
                                            <>
                                                <span className='_flex w-full items-center justify-between gap-2'>
                                                    <span>{option.value}</span>
                                                    {selected && (
                                                        <CheckIcon
                                                            className='h-4 w-4 shrink-0 text-primary'
                                                            aria-hidden='true'
                                                        />
                                                    )}
                                                </span>
                                            </>
                                        )}
                                    </HUICombobox.Option>
                                ))
                            )}
                        </HUICombobox.Options>
                    </Transition>
                </div>
            </HUICombobox>
        </div>
    );
}
